// useRole.ts — Role & permission React hooks
//
// Reads the current user's role from useGetMe (server-authoritative).
// No client-side elevation is possible — the role comes exclusively from /auth/me.

import { useGetMe, getGetMeQueryKey } from '@workspace/api-client-react';
import { hasRequiredRole, isMCUnlocked, type UserRole } from '@/lib/permissions';

// ─── Extended user type ───────────────────────────────────────────────────────
// The generated User type predates the role / avatarUrl fields.
// This reflects what /auth/me actually returns after the RBAC migration.
export interface AuthUser {
  id: number;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  role: UserRole;
  createdAt: string;
}

// ─── Core hooks ───────────────────────────────────────────────────────────────

/** Returns the current user's role. Falls back to 'USER' during load or when unauthenticated. */
export function useRole(): UserRole {
  const { data } = useGetMe({
    query: { queryKey: getGetMeQueryKey(), retry: false },
  });
  return ((data as unknown as AuthUser | undefined)?.role ?? 'USER') as UserRole;
}

/** True if the current user meets or exceeds `minRole`. Returns false during load (fail-closed). */
export function useCanAccess(minRole: UserRole): boolean {
  return hasRequiredRole(useRole(), minRole);
}

/** True when the authenticated user's role is OWNER. */
export function useIsOwner(): boolean { return useRole() === 'OWNER'; }

/** True when the role is BETA_TESTER or above. */
export function useIsBetaTester(): boolean { return useCanAccess('BETA_TESTER'); }

/**
 * True when Mission Control should be shown in the sidebar:
 *   role === OWNER  AND  the session PIN has been entered this session.
 * Note: the server always re-validates the role — this is UI-only convenience.
 */
export function useMCVisible(): boolean {
  return useIsOwner() && isMCUnlocked();
}

/** Returns the full user object with RBAC fields. Null while loading or unauthenticated. */
export function useAuthUser(): AuthUser | null {
  const { data } = useGetMe({
    query: { queryKey: getGetMeQueryKey(), retry: false },
  });
  return data ? (data as unknown as AuthUser) : null;
}
